<template>
  <form @submit.prevent="handleSubmit">
    <!-- Tab Navigation -->
    <ul class="nav nav-tabs mb-4" role="tablist">
      <li class="nav-item" role="presentation">
        <button
          class="nav-link active"
          type="button"
          data-bs-toggle="tab"
          data-bs-target="#basic-info"
          role="tab"
        >
          <i class="fas fa-info-circle me-1"></i>
          Basic Info
        </button>
      </li>
      <li class="nav-item" role="presentation">
        <button
          class="nav-link"
          type="button"
          data-bs-toggle="tab"
          data-bs-target="#content"
          role="tab"
        >
          <i class="fas fa-file-text me-1"></i>
          Content
        </button>
      </li>
      <li class="nav-item" role="presentation">
        <button
          class="nav-link"
          type="button"
          data-bs-toggle="tab"
          data-bs-target="#media"
          role="tab"
        >
          <i class="fas fa-images me-1"></i>
          Media
        </button>
      </li>
      <li class="nav-item" role="presentation">
        <button
          class="nav-link"
          type="button"
          data-bs-toggle="tab"
          data-bs-target="#seo"
          role="tab"
        >
          <i class="fas fa-search me-1"></i>
          SEO & Social
        </button>
      </li>
      <li class="nav-item" role="presentation">
        <button
          class="nav-link"
          type="button"
          data-bs-toggle="tab"
          data-bs-target="#settings"
          role="tab"
        >
          <i class="fas fa-cog me-1"></i>
          Settings
        </button>
      </li>
    </ul>

    <!-- Tab Content -->
    <div class="tab-content">
      <!-- Basic Info Tab -->
      <div class="tab-pane fade show active" id="basic-info" role="tabpanel">
        <div class="row">
          <div class="col-md-8">
            <div class="mb-3">
              <label for="title" class="form-label required">Blog Title</label>
              <input
                type="text"
                class="form-control"
                id="title"
                v-model="formData.title"
                @input="generateSlug"
                required
                maxlength="200"
              />
              <div class="form-text">
                {{ formData.title?.length || 0 }}/200 characters
              </div>
            </div>

            <div class="mb-3">
              <label for="slug" class="form-label required">Slug</label>
              <input
                type="text"
                class="form-control"
                id="slug"
                v-model="formData.slug"
                required
                pattern="^[a-z0-9-]+$"
                title="Only lowercase letters, numbers, and hyphens are allowed"
              />
              <div class="form-text">
                URL-friendly version of the title (lowercase, hyphens only)
              </div>
            </div>

            <div class="mb-3">
              <label for="excerpt" class="form-label">Excerpt</label>
              <textarea
                class="form-control"
                id="excerpt"
                v-model="formData.excerpt"
                rows="3"
                maxlength="300"
                placeholder="Brief description of the blog post..."
              ></textarea>
              <div class="form-text">
                {{ formData.excerpt?.length || 0 }}/300 characters
              </div>
            </div>
          </div>

          <div class="col-md-4">
            <div class="mb-3">
              <label for="category_id" class="form-label required"
                >Category</label
              >
              <select
                class="form-select"
                id="category_id"
                v-model="formData.category_id"
                required
              >
                <option value="">Select Category</option>
                <option
                  v-for="category in categories"
                  :key="category._id"
                  :value="category._id"
                >
                  {{ category.title }}
                </option>
              </select>
            </div>

            <div class="mb-3">
              <label for="tags" class="form-label">Tags</label>
              <input
                type="text"
                class="form-control"
                id="tags"
                v-model="tagsInput"
                placeholder="Enter tags separated by commas"
              />
              <div class="form-text">Separate multiple tags with commas</div>
              <div class="mt-2" v-if="formData.tags && formData.tags.length">
                <span
                  v-for="(tag, index) in formData.tags"
                  :key="index"
                  class="badge bg-secondary me-1 mb-1"
                >
                  {{ tag }}
                  <button
                    type="button"
                    class="btn-close btn-close-white ms-1"
                    @click="removeTag(index)"
                    style="font-size: 0.7em"
                  ></button>
                </span>
              </div>
            </div>

            <div class="mb-3">
              <label for="status" class="form-label">Status</label>
              <select class="form-select" id="status" v-model="formData.status">
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="scheduled">Scheduled</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            <div class="mb-3" v-if="formData.status === 'scheduled'">
              <label for="scheduled_at" class="form-label">Schedule Date</label>
              <input
                type="datetime-local"
                class="form-control"
                id="scheduled_at"
                v-model="scheduledAtInput"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Content Tab -->
      <div class="tab-pane fade" id="content" role="tabpanel">
        <div class="mb-3">
          <label for="content" class="form-label required">Blog Content</label>
          <RichTextEditor
            v-model="formData.content"
            placeholder="Write your blog content here..."
            height="400px"
          />
          <div class="form-text">
            Use the rich text editor to format your content. Reading time will
            be calculated automatically.
          </div>
        </div>
      </div>

      <!-- Media Tab -->
      <div class="tab-pane fade" id="media" role="tabpanel">
        <div class="row">
          <div class="col-md-6">
            <h6>Featured Image</h6>
            <div class="mb-3">
              <label for="featured_image_file" class="form-label"
                >Upload Featured Image</label
              >
              <input
                type="file"
                class="form-control"
                id="featured_image_file"
                @change="onFeaturedImageSelect"
                accept="image/*"
              />
            </div>

            <div class="mb-3">
              <label for="featured_image_url" class="form-label"
                >Or Enter Image URL</label
              >
              <input
                type="url"
                class="form-control"
                id="featured_image_url"
                v-model="formData.featured_image"
                placeholder="https://example.com/image.jpg"
              />
            </div>

            <div class="mb-3">
              <label for="featured_image_alt" class="form-label"
                >Alt Text</label
              >
              <input
                type="text"
                class="form-control"
                id="featured_image_alt"
                v-model="formData.featured_image_alt"
                maxlength="100"
                placeholder="Describe the image for accessibility"
              />
            </div>

            <div v-if="formData.featured_image" class="mb-3">
              <img
                :src="formData.featured_image"
                :alt="formData.featured_image_alt"
                class="img-thumbnail"
                style="max-width: 200px; max-height: 150px"
              />
            </div>
          </div>

          <div class="col-md-6">
            <h6>Gallery Images</h6>
            <div class="mb-3">
              <label for="gallery_image_file" class="form-label"
                >Upload Gallery Image</label
              >
              <input
                type="file"
                class="form-control"
                id="gallery_image_file"
                @change="onGalleryImageSelect"
                accept="image/*"
              />
            </div>

            <div class="gallery-images mt-3">
              <div
                v-for="(image, index) in formData.gallery_images"
                :key="index"
                class="d-flex align-items-start mb-3 p-2 border rounded"
              >
                <img
                  :src="image.url"
                  :alt="image.alt"
                  class="img-thumbnail me-3"
                  style="width: 80px; height: 60px; object-fit: cover"
                />
                <div class="flex-grow-1">
                  <input
                    type="text"
                    class="form-control form-control-sm mb-2"
                    v-model="image.alt"
                    placeholder="Alt text"
                    maxlength="100"
                  />
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    v-model="image.caption"
                    placeholder="Caption"
                    maxlength="200"
                  />
                </div>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger ms-2"
                  @click="removeGalleryImage(index)"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- SEO Tab -->
      <div class="tab-pane fade" id="seo" role="tabpanel">
        <div class="row">
          <div class="col-md-6">
            <h6>Search Engine Optimization</h6>
            <div class="mb-3">
              <label for="meta_title" class="form-label">Meta Title</label>
              <input
                type="text"
                class="form-control"
                id="meta_title"
                v-model="formData.meta_title"
                maxlength="60"
                placeholder="SEO title for search engines"
              />
              <div class="form-text">
                {{ formData.meta_title?.length || 0 }}/60 characters
              </div>
            </div>

            <div class="mb-3">
              <label for="meta_description" class="form-label"
                >Meta Description</label
              >
              <textarea
                class="form-control"
                id="meta_description"
                v-model="formData.meta_description"
                rows="3"
                maxlength="160"
                placeholder="Description for search engine results"
              ></textarea>
              <div class="form-text">
                {{ formData.meta_description?.length || 0 }}/160 characters
              </div>
            </div>

            <div class="mb-3">
              <label for="meta_keywords" class="form-label"
                >Meta Keywords</label
              >
              <input
                type="text"
                class="form-control"
                id="meta_keywords"
                v-model="metaKeywordsInput"
                placeholder="keyword1, keyword2, keyword3"
              />
            </div>

            <div class="mb-3">
              <label for="canonical_url" class="form-label"
                >Canonical URL</label
              >
              <input
                type="url"
                class="form-control"
                id="canonical_url"
                v-model="formData.canonical_url"
                placeholder="https://example.com/canonical-url"
              />
            </div>
          </div>

          <div class="col-md-6">
            <h6>Social Media</h6>
            <div class="mb-3">
              <label for="og_title" class="form-label">Open Graph Title</label>
              <input
                type="text"
                class="form-control"
                id="og_title"
                v-model="formData.og_title"
                maxlength="60"
                placeholder="Title for social media sharing"
              />
            </div>

            <div class="mb-3">
              <label for="og_description" class="form-label"
                >Open Graph Description</label
              >
              <textarea
                class="form-control"
                id="og_description"
                v-model="formData.og_description"
                rows="3"
                maxlength="160"
                placeholder="Description for social media sharing"
              ></textarea>
            </div>

            <div class="mb-3">
              <label for="og_image" class="form-label">Open Graph Image</label>
              <input
                type="url"
                class="form-control"
                id="og_image"
                v-model="formData.og_image"
                placeholder="https://example.com/og-image.jpg"
              />
            </div>

            <div class="mb-3">
              <label for="twitter_title" class="form-label"
                >Twitter Title</label
              >
              <input
                type="text"
                class="form-control"
                id="twitter_title"
                v-model="formData.twitter_title"
                maxlength="60"
                placeholder="Title for Twitter sharing"
              />
            </div>

            <div class="mb-3">
              <label for="twitter_description" class="form-label"
                >Twitter Description</label
              >
              <textarea
                class="form-control"
                id="twitter_description"
                v-model="formData.twitter_description"
                rows="3"
                maxlength="160"
                placeholder="Description for Twitter sharing"
              ></textarea>
            </div>

            <div class="mb-3">
              <label for="twitter_image" class="form-label"
                >Twitter Image</label
              >
              <input
                type="url"
                class="form-control"
                id="twitter_image"
                v-model="formData.twitter_image"
                placeholder="https://example.com/twitter-image.jpg"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Settings Tab -->
      <div class="tab-pane fade" id="settings" role="tabpanel">
        <div class="row">
          <div class="col-md-6">
            <div class="form-check form-switch mb-3">
              <input
                class="form-check-input"
                type="checkbox"
                id="is_featured"
                v-model="formData.is_featured"
              />
              <label class="form-check-label" for="is_featured">
                Featured Blog
              </label>
            </div>

            <div class="form-check form-switch mb-3">
              <input
                class="form-check-input"
                type="checkbox"
                id="is_trending"
                v-model="formData.is_trending"
              />
              <label class="form-check-label" for="is_trending">
                Trending Blog
              </label>
            </div>

            <div class="form-check form-switch mb-3">
              <input
                class="form-check-input"
                type="checkbox"
                id="allow_comments"
                v-model="formData.allow_comments"
              />
              <label class="form-check-label" for="allow_comments">
                Allow Comments
              </label>
            </div>

            <div class="form-check form-switch mb-3">
              <input
                class="form-check-input"
                type="checkbox"
                id="is_active"
                v-model="formData.is_active"
              />
              <label class="form-check-label" for="is_active"> Active </label>
            </div>
          </div>

          <div class="col-md-6">
            <div class="mb-3">
              <label for="reading_time" class="form-label"
                >Reading Time (minutes)</label
              >
              <input
                type="number"
                class="form-control"
                id="reading_time"
                v-model.number="formData.reading_time"
                min="1"
                placeholder="Will be calculated automatically"
              />
              <div class="form-text">
                Leave empty for automatic calculation based on content length
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Statistics (Read-only)</label>
              <div class="row">
                <div class="col-4">
                  <small class="text-muted">Views</small>
                  <div class="fw-bold">{{ formData.view_count || 0 }}</div>
                </div>
                <div class="col-4">
                  <small class="text-muted">Likes</small>
                  <div class="fw-bold">{{ formData.like_count || 0 }}</div>
                </div>
                <div class="col-4">
                  <small class="text-muted">Shares</small>
                  <div class="fw-bold">{{ formData.share_count || 0 }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Form Actions -->
    <div class="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
      <button type="button" class="btn btn-secondary" @click="$emit('cancel')">
        Cancel
      </button>
      <button type="submit" class="btn btn-primary" :disabled="loading">
        <span
          v-if="loading"
          class="spinner-border spinner-border-sm me-2"
        ></span>
        {{ isEdit ? "Update Blog" : "Create Blog" }}
      </button>
    </div>
  </form>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, onMounted } from "vue";
import apiService from "@/services/apiService";
import RichTextEditor from "@/components/commen/RichTextEditor.vue";

// Props
interface Props {
  blog?: any;
  categories: any[];
}

const props = withDefaults(defineProps<Props>(), {
  blog: null,
  categories: () => [],
});

// Emits
const emit = defineEmits<{
  save: [data: any];
  cancel: [];
}>();

// State
const loading = ref(false);
const tagsInput = ref("");
const metaKeywordsInput = ref("");
const scheduledAtInput = ref("");

// Form data
const formData = reactive({
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  featured_image: "",
  featured_image_alt: "",
  gallery_images: [] as Array<{ url: string; alt: string; caption: string }>,
  category_id: "",
  tags: [] as string[],
  status: "draft",
  published_at: "",
  scheduled_at: "",
  reading_time: 0,
  is_featured: false,
  is_trending: false,
  allow_comments: true,
  meta_title: "",
  meta_description: "",
  meta_keywords: [] as string[],
  canonical_url: "",
  og_title: "",
  og_description: "",
  og_image: "",
  twitter_title: "",
  twitter_description: "",
  twitter_image: "",
  is_active: true,
  view_count: 0,
  like_count: 0,
  share_count: 0,
});

// Computed
const isEdit = computed(() => !!props.blog?._id);

// Methods
const generateSlug = () => {
  if (formData.title && !isEdit.value) {
    formData.slug = formData.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  }
};

const onFeaturedImageSelect = async (event: Event) => {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (file) {
    try {
      loading.value = true;
      const response = await apiService.uploadFile("upload/file-common", file);
      formData.featured_image = response.url;
    } catch (error) {
      console.error("Error uploading featured image:", error);
      alert("Error uploading image. Please try again.");
    } finally {
      loading.value = false;
    }
  }
};

const onGalleryImageSelect = async (event: Event) => {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (file) {
    try {
      loading.value = true;
      const response = await apiService.uploadFile("upload/file-common", file);
      formData.gallery_images.push({
        url: response.url,
        alt: "",
        caption: "",
      });
      // Reset the input
      (event.target as HTMLInputElement).value = "";
    } catch (error) {
      console.error("Error uploading gallery image:", error);
      alert("Error uploading image. Please try again.");
    } finally {
      loading.value = false;
    }
  }
};

const removeGalleryImage = (index: number) => {
  formData.gallery_images.splice(index, 1);
};

const removeTag = (index: number) => {
  formData.tags.splice(index, 1);
  updateTagsInput();
};

const updateTagsInput = () => {
  tagsInput.value = formData.tags.join(", ");
};

const updateMetaKeywordsInput = () => {
  metaKeywordsInput.value = formData.meta_keywords.join(", ");
};

const handleSubmit = async () => {
  try {
    loading.value = true;

    // Process tags
    if (tagsInput.value) {
      formData.tags = tagsInput.value
        .split(",")
        .map((tag) => tag.trim().toLowerCase())
        .filter((tag) => tag.length > 0);
    }

    // Process meta keywords
    if (metaKeywordsInput.value) {
      formData.meta_keywords = metaKeywordsInput.value
        .split(",")
        .map((keyword) => keyword.trim())
        .filter((keyword) => keyword.length > 0);
    }

    // Process scheduled date
    if (scheduledAtInput.value) {
      formData.scheduled_at = new Date(scheduledAtInput.value).toISOString();
    }

    emit("save", { ...formData });
  } catch (error) {
    console.error("Error preparing blog data:", error);
  } finally {
    loading.value = false;
  }
};

// Initialize form data when blog prop changes
watch(
  () => props.blog,
  (newBlog) => {
    if (newBlog) {
      // Map basic fields
      Object.assign(formData, {
        ...newBlog,
        // Extract ID if category_id is an object
        category_id: typeof newBlog.category_id === 'object' && newBlog.category_id !== null
          ? newBlog.category_id._id 
          : newBlog.category_id || "",
        gallery_images: Array.isArray(newBlog.gallery_images) ? [...newBlog.gallery_images] : [],
        tags: Array.isArray(newBlog.tags) ? [...newBlog.tags] : [],
        meta_keywords: Array.isArray(newBlog.meta_keywords) ? [...newBlog.meta_keywords] : [],
      });

      // Update input fields for tags and keywords
      updateTagsInput();
      updateMetaKeywordsInput();

      // Handle scheduled_at input
      if (newBlog.scheduled_at) {
        try {
          scheduledAtInput.value = new Date(newBlog.scheduled_at)
            .toISOString()
            .slice(0, 16);
        } catch (e) {
          console.error("Invalid scheduled date:", newBlog.scheduled_at);
          scheduledAtInput.value = "";
        }
      } else {
        scheduledAtInput.value = "";
      }
    }
  },
  { immediate: true }
);

// Expose methods to parent components
defineExpose({
  handleSubmit,
});
</script>

<style scoped>
.required::after {
  content: " *";
  color: red;
}

.nav-tabs .nav-link {
  color: #6c757d;
}

.nav-tabs .nav-link.active {
  color: #007bff;
  background-color: #fff;
  border-color: #dee2e6 #dee2e6 #fff;
}

.gallery-images {
  max-height: 400px;
  overflow-y: auto;
}

.badge .btn-close {
  margin-left: 4px;
}
</style>
